import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { SocialPostImage } from '../../../types/social'

export interface PostImagesProps {
  images?: SocialPostImage[] | null
  className?: string
  /** Lista de moderação: thumb 48×48 */
  compact?: boolean
}

const PostImages: React.FC<PostImagesProps> = ({
  images,
  className,
  compact = false,
}) => {
  const list = (images || []).filter((img) => img.url)
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const close = useCallback(() => setOpenIndex(null), [])

  const goPrev = useCallback(() => {
    setOpenIndex((idx) => {
      if (idx === null || list.length < 2) return idx
      return (idx - 1 + list.length) % list.length
    })
  }, [list.length])

  const goNext = useCallback(() => {
    setOpenIndex((idx) => {
      if (idx === null || list.length < 2) return idx
      return (idx + 1) % list.length
    })
  }, [list.length])

  useEffect(() => {
    if (openIndex === null) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      else if (e.key === 'ArrowLeft') goPrev()
      else if (e.key === 'ArrowRight') goNext()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [openIndex, close, goPrev, goNext])

  if (list.length === 0) return null

  if (compact) {
    const first = list[0]
    return (
      <div className={clsx('inline-flex items-center gap-2', className)}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setOpenIndex(0)
          }}
          className="relative h-12 w-12 shrink-0 overflow-hidden rounded-md border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          aria-label={`Ampliar ${first.file_name || 'imagem'}`}
        >
          <img
            src={first.url}
            alt={first.file_name || 'Imagem da postagem'}
            className="h-full w-full object-cover"
          />
          {list.length > 1 && (
            <span className="absolute bottom-0 right-0 rounded-tl bg-black/60 px-1 text-[10px] font-medium text-white">
              +{list.length - 1}
            </span>
          )}
        </button>
        {openIndex !== null &&
          createPortal(
            <ImageLightbox
              images={list}
              index={openIndex}
              onClose={close}
              onPrev={goPrev}
              onNext={goNext}
            />,
            document.body
          )}
      </div>
    )
  }

  const count = list.length

  return (
    <div className={clsx('mt-2', className)}>
      {count === 1 ? (
        <button
          type="button"
          onClick={() => setOpenIndex(0)}
          className={clsx(
            'group relative w-full overflow-hidden rounded-xl',
            'bg-gray-100 dark:bg-gray-800/80',
            'max-h-[280px] sm:max-h-[360px]',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
          )}
          aria-label={`Ampliar ${list[0].file_name || 'imagem'}`}
        >
          <img
            src={list[0].url}
            alt={list[0].file_name || 'Imagem da postagem'}
            className="mx-auto block max-h-[280px] sm:max-h-[360px] w-full object-contain"
          />
        </button>
      ) : (
        <div
          className={clsx(
            'grid gap-1 overflow-hidden rounded-xl',
            count === 2 && 'grid-cols-2',
            count === 3 && 'grid-cols-2',
            count >= 4 && 'grid-cols-2'
          )}
        >
          {list.slice(0, 4).map((img, index) => (
            <button
              type="button"
              key={img.id || `${img.file_path}-${index}`}
              onClick={() => setOpenIndex(index)}
              className={clsx(
                'relative overflow-hidden bg-gray-100 dark:bg-gray-800/80',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:z-10',
                count === 3 && index === 0 && 'row-span-2 max-h-[280px] sm:max-h-[360px]',
                count === 3 && index > 0 && 'h-[138px] sm:h-[178px]',
                count !== 3 && 'h-[140px] sm:h-[180px]',
                count >= 4 && 'h-[140px] sm:h-[180px]'
              )}
              aria-label={`Ampliar ${img.file_name || `imagem ${index + 1}`}`}
            >
              <img
                src={img.url}
                alt={img.file_name || `Imagem ${index + 1} da postagem`}
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {openIndex !== null &&
        createPortal(
          <ImageLightbox
            images={list}
            index={openIndex}
            onClose={close}
            onPrev={goPrev}
            onNext={goNext}
          />,
          document.body
        )}
    </div>
  )
}

interface ImageLightboxProps {
  images: SocialPostImage[]
  index: number
  onClose: () => void
  onPrev: () => void
  onNext: () => void
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({
  images,
  index,
  onClose,
  onPrev,
  onNext,
}) => {
  const current = images[index]
  if (!current?.url) return null
  const multi = images.length > 1

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-3 sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Imagem ampliada"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-3 right-3 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        aria-label="Fechar"
      >
        <XMarkIcon className="h-6 w-6" />
      </button>

      {multi && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onPrev()
            }}
            className="absolute left-2 sm:left-4 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Imagem anterior"
          >
            <ChevronLeftIcon className="h-7 w-7" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onNext()
            }}
            className="absolute right-2 sm:right-4 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Próxima imagem"
          >
            <ChevronRightIcon className="h-7 w-7" />
          </button>
        </>
      )}

      <div
        className="relative flex max-h-[85vh] max-w-[90vw] items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={current.url}
          alt={current.file_name || 'Imagem ampliada'}
          className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
        />
        {multi && (
          <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-sm text-white/80 tabular-nums">
            {index + 1} / {images.length}
          </span>
        )}
      </div>
    </div>
  )
}

export default PostImages
